import { useState, useEffect, useCallback } from "react";
import { fetchReview, submitDecision, ReviewResponse, SubmitDecision } from "../lib/api";

type ReviewState =
  | { status: "loading" }
  | { status: "error"; error: "NOT_FOUND" | "EXPIRED" | "UNAUTHORIZED" | "FORBIDDEN" | "UNKNOWN" }
  | { status: "loaded"; review: ReviewResponse }
  | { status: "submitting"; review: ReviewResponse }
  | { status: "submitted"; decision: SubmitDecision["decision"]; message: string };

export function useReview(token: string, refreshKey: number = 0) {
  const [state, setState] = useState<ReviewState>({ status: "loading" });

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    setState({ status: "loading" });

    async function load() {
      try {
        const review = await fetchReview(token);
        if (cancelled) return;

        if (review.status === "expired") {
          setState({ status: "error", error: "EXPIRED" });
        } else if (!review.can_submit) {
          setState({
            status: "submitted",
            decision: review.status as SubmitDecision["decision"],
            message: "Review already completed",
          });
        } else {
          setState({ status: "loaded", review });
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "UNKNOWN";
        if (message === "NOT_FOUND") {
          setState({ status: "error", error: "NOT_FOUND" });
        } else if (message === "EXPIRED") {
          setState({ status: "error", error: "EXPIRED" });
        } else if (message === "UNAUTHORIZED") {
          setState({ status: "error", error: "UNAUTHORIZED" });
        } else if (message === "FORBIDDEN") {
          setState({ status: "error", error: "FORBIDDEN" });
        } else {
          setState({ status: "error", error: "UNKNOWN" });
        }
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [token, refreshKey]);

  const submit = useCallback(
    async (decision: SubmitDecision["decision"], generalNotes?: string) => {
      if (state.status !== "loaded") return;

      setState({ status: "submitting", review: state.review });

      try {
        const result = await submitDecision(token, {
          decision,
          general_notes: generalNotes,
        });
        setState({ status: "submitted", decision, message: result.message });
      } catch (err) {
        const message = err instanceof Error ? err.message : "UNKNOWN";
        if (message === "UNAUTHORIZED") {
          setState({ status: "error", error: "UNAUTHORIZED" });
        } else if (message === "ALREADY_DECIDED") {
          setState({
            status: "submitted",
            decision,
            message: "Review already completed by another reviewer",
          });
        } else {
          setState({ status: "loaded", review: state.review });
          throw err;
        }
      }
    },
    [token, state]
  );

  return { state, submit };
}
